import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ArrowLeft, X } from 'lucide-react';
import AgentSidebar from './AgentSidebar';
import NotificationBell from './NotificationBell';

const AgentLayout = ({ title, children }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [title]);

  // Responsive: auto-collapse on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) setCollapsed(true);
      else setCollapsed(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="agent-layout">
      {/* Desktop / Tablet Sidebar */}
      <div className="hidden md:block">
        <AgentSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)}></div>
          <div className="relative z-10 w-60">
            <AgentSidebar collapsed={false} setCollapsed={() => {}} />
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-[-48px] w-11 h-11 bg-white rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              data-testid="mobile-sidebar-close"
            >
              <X size={20} className="text-gray-700" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? 'md:ml-[68px]' : 'md:ml-60'}`}>
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30 print:hidden" data-testid="agent-top-header">
          <div className="flex items-center justify-between px-4 md:px-6 py-3">
            <div className="flex items-center gap-3">
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition active:scale-95"
                data-testid="mobile-menu-btn"
              >
                <Menu size={22} />
              </button>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-brand-blue min-h-[44px] px-2 rounded transition"
                data-testid="nav-back-btn"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="h-5 w-px bg-gray-200 hidden sm:block"></div>
              <h1 className="text-lg font-bold text-gray-900" data-testid="page-title">{title}</h1>
            </div>
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AgentLayout;
